import { useState, type MouseEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { formatDistanceLabel, type ResolvedLocation } from "@/lib/geo";
import { stripDemoMarker } from "@/lib/demo";
import { Check, Heart, MapPin, Star, Truck, Sprout, Landmark } from "lucide-react";
import { FoundingMakerBadge } from "@/components/FoundingMakerBadge";
import { HeatBadge } from "@/components/HeatBadge";
import { WeightSizePicker, type ResolvedWeightUnit } from "@/components/WeightSizePicker";
import { formatWeight } from "@shared/weight";
import type { Item, User } from "@shared/schema";
import { isRequestableStatus, hasListingPhoto } from "@shared/schema";
import { getListingImages } from "@shared/clothing";
import { ListingPhotoPlaceholder } from "@/components/ListingPhotoPlaceholder";
import { isSnapEligibleItem, isEbtVisible } from "@shared/snap";

type BuyerFulfillmentOption = {
  id: string;
  fulfillmentMethod: "seller_pickup" | "drop_zone_pickup" | "local_delivery";
  buyerLabel: string;
  description?: string | null;
  dayLabel?: string | null;
  timeSlots?: {
    id: string;
    dayOfWeek: string;
    startTime: string;
    endTime: string;
  }[];
  dropZone?: {
    id: string;
    name: string;
    city: string;
    state: string;
    zipCode: string;
    pickupWindowLabel?: string | null;
  } | null;
};

interface ProductCardProps {
  item: Item & { seller?: (User & { averageRating?: number; reviewCount?: number }) | null };
  onAddToCart: (itemId: string, qty: number, maxQty?: number, weightUnit?: ResolvedWeightUnit) => void;
  buyerLocation?: ResolvedLocation | null;
  fallbackBuyerZip?: string | null;
  isSaved?: boolean;
  saveCount?: number;
  onToggleSave?: (itemId: string) => void;
  cartQty?: number;
}

export function ProductCard({
  item,
  onAddToCart,
  buyerLocation,
  fallbackBuyerZip,
  isSaved,
  saveCount,
  onToggleSave,
  cartQty = 0,
}: ProductCardProps) {
  const [showCheck, setShowCheck] = useState(false);
  const [heartPop, setHeartPop] = useState(false);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [sizePickerOpen, setSizePickerOpen] = useState(false);
  const [location, setLocation] = useLocation();

  const soldByWeight = Boolean((item as any).soldByWeight);
  const weightMin = (item as any).weightMin as number | null | undefined;
  const weightMax = (item as any).weightMax as number | null | undefined;
  const unitsAvailable = ((item as any).unitsAvailable as number | null | undefined) ?? 0;
  const sizeTier = (item as any).sizeTier as string | null | undefined;
  const isSoldOut = soldByWeight ? unitsAvailable <= 0 : item.qtyAvailable <= 0;

  const price = parseFloat(item.price);
  const groceryAvg = item.groceryAvg ? parseFloat(item.groceryAvg) : null;
  const savings = groceryAvg && groceryAvg > price ? groceryAvg - price : null;

  const atStockCap = item.qtyAvailable > 0 && cartQty >= item.qtyAvailable;

  const handleAddToCart = () => {
    if (atStockCap) return;
    onAddToCart(item.id, 1, item.qtyAvailable);
    setShowCheck(true);
    setTimeout(() => setShowCheck(false), 500);
  };

  const itemDetailHref = `/items/${item.id}?back=${encodeURIComponent(
    location + (typeof window !== "undefined" ? window.location.search : "")
  )}`;

  const stopCardNavigation = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
  };

  const goToProductDetail = () => {
    setLocation(itemDetailHref);
  };

  const displayItemName = stripDemoMarker(item.name) || item.name;
  const sellerName = item.seller
    ? stripDemoMarker(
        item.seller.displayName ||
          `${item.seller.firstName || ""} ${item.seller.lastName || ""}`
      ) || "Local Producer"
    : "Local Producer";

  // Cached app-wide (App.tsx fetches the same key at startup) — no extra
  // request per card in practice.
  const { data: publicConfig } = useQuery<{ snapPlatformApproved?: boolean }>({
    queryKey: ["/api/public/config"],
  });
  const isEbtEligible =
    isSnapEligibleItem(item.category, (item as any).subtype) &&
    isEbtVisible(Number((item.seller as any)?.snapApproved ?? 0) === 1, publicConfig?.snapPlatformApproved ?? false);

  const { data: fulfillmentOptions = [] } = useQuery<BuyerFulfillmentOption[]>({
    queryKey: ["/api/items/fulfillment-options", item.id],
    queryFn: async () => {
      const res = await fetch(`/api/items/${item.id}/fulfillment-options`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
  });

  const sellerLatitude =
    (item.seller as any)?.latitude == null || (item.seller as any)?.latitude === ""
      ? null
      : Number((item.seller as any).latitude);
  const sellerLongitude =
    (item.seller as any)?.longitude == null || (item.seller as any)?.longitude === ""
      ? null
      : Number((item.seller as any).longitude);

  const sellerResolvedLocation =
    Number.isFinite(sellerLatitude) && Number.isFinite(sellerLongitude)
      ? {
          source: "producer_address" as const,
          label: sellerName,
          zipCode: item.seller?.zipCode ?? item.zipCode ?? null,
          point: {
            latitude: sellerLatitude as number,
            longitude: sellerLongitude as number,
          },
        }
      : null;

  const productDistanceLabel = formatDistanceLabel({
    buyerLocation,
    producerLocation: sellerResolvedLocation,
    fallbackBuyerZip: fallbackBuyerZip ?? null,
    fallbackProducerZip: item.seller?.zipCode ?? item.zipCode ?? null,
  });

  const primaryOption = fulfillmentOptions[0];
  const attributes =
    item.attributesJson && typeof item.attributesJson === "object"
      ? (item.attributesJson as {
          bundleSize?: string | null;
          harvestTiming?: string | null;
          quickDetails?: string[] | null;
        })
      : null;
  const shortDescription =
    typeof item.shortDescription === "string" ? item.shortDescription.trim() : "";
  const bundleSize =
    typeof attributes?.bundleSize === "string" ? attributes.bundleSize.trim() : "";
  const harvestTiming =
    typeof attributes?.harvestTiming === "string" ? attributes.harvestTiming.trim() : "";
  const cardMetaLine = [bundleSize, harvestTiming].filter(Boolean).join(" • ");
  const quickDetails = Array.isArray(attributes?.quickDetails)
    ? attributes!.quickDetails.filter((value): value is string => typeof value === "string" && value.trim().length > 0).slice(0, 1)
    : [];
  const supportLine = cardMetaLine || shortDescription;
  const isMadeToOrder = (item as any).isMadeToOrder === 1;
  const mtoLeadTimeDays = (item as any).leadTimeDays ?? null;
  const mtoReadyDate = isMadeToOrder && mtoLeadTimeDays
    ? new Date(Date.now() + mtoLeadTimeDays * 86_400_000)
    : null;
  const weightRangeLabel =
    soldByWeight && weightMin != null && weightMax != null
      ? weightMin === weightMax
        ? formatWeight(weightMin)
        : `${formatWeight(weightMin)}–${formatWeight(weightMax)}`
      : null;
  const stockLabel = soldByWeight
    ? unitsAvailable <= 0
      ? "Sold out"
      : `${unitsAvailable} left`
    : isMadeToOrder
      ? "Made to order"
      : item.qtyAvailable <= 0
        ? "Out of stock"
        : atStockCap
          ? `Max in cart (${item.qtyAvailable})`
          : `${item.qtyAvailable} available`;

  const sellerReviewCount = item.seller?.reviewCount ?? 0;
  const sellerAverageRating = item.seller?.averageRating ?? 0;
  const sellerReviewsHref = item.seller?.id
    ? `/u/${item.seller.id}?back=${encodeURIComponent(location + (typeof window !== "undefined" ? window.location.search : ""))}#reviews`
    : null;

  const postedLabel = (() => {
    if (!item.createdAt) return null;
    const diff = Date.now() - new Date(item.createdAt).getTime();
    const days = Math.floor(diff / 86_400_000);
    if (days < 1) return "Posted today";
    if (days === 1) return "Posted yesterday";
    if (days < 7) return `Posted ${days}d ago`;
    const weeks = Math.floor(days / 7);
    if (weeks < 5) return `Posted ${weeks}w ago`;
    return null;
  })();
  const imageLocationLabel =
    productDistanceLabel || (item.zipCode ? `ZIP ${item.zipCode}` : "");

  return (
    <div
      className={`relative cursor-pointer overflow-hidden rounded-[1.7rem] border border-card-border bg-card shadow-[0_18px_42px_rgba(90,72,40,0.16),0_6px_16px_rgba(90,72,40,0.10)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_22px_52px_rgba(90,72,40,0.20),0_10px_22px_rgba(90,72,40,0.12)] ${
        isSoldOut ? "opacity-90" : ""
      }`}
      data-testid={`card-product-${item.id}`}
      onClick={goToProductDetail}
      role="link"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          goToProductDetail();
        }
      }}
      aria-label={`View details for ${displayItemName}`}
    >
      <div className="relative flex flex-row items-stretch">
        <div className="relative w-[36%] min-w-[118px] max-w-[164px] flex-shrink-0 overflow-hidden bg-muted/55 sm:w-[31%] sm:min-w-[144px] sm:max-w-[198px]">
          {hasListingPhoto(item) ? (
            <img
              src={getListingImages(item)[0]}
              alt={displayItemName}
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover object-center"
            />
          ) : (
            <ListingPhotoPlaceholder category={item.category} size="sm" />
          )}
          {isMadeToOrder ? (
            <Badge
              className="absolute left-2 top-2 z-20 border-0 bg-primary text-primary-foreground"
              data-testid={`badge-mto-${item.id}`}
            >
              {mtoReadyDate
                ? `Made to order · ready ~${mtoReadyDate.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`
                : "Made to order"}
            </Badge>
          ) : (item as any).growthState === "growing" && (item as any).estimatedRipeDate ? (
            <Badge
              className="absolute left-2 top-2 z-20 border-0 bg-emerald-600 text-white"
              data-testid={`badge-ripe-eta-${item.id}`}
            >
              Ready ~{new Date((item as any).estimatedRipeDate).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
            </Badge>
          ) : item.freshnessNote && item.qtyAvailable > 0 ? (
            <Badge
              className="absolute left-2 top-2 z-20 border-0 bg-primary/90 text-primary-foreground"
              data-testid={`badge-freshness-${item.id}`}
            >
              {item.freshnessNote}
            </Badge>
          ) : null}

          {(item as any).isGlutenFree === 1 && (
            <Badge
              className="absolute right-2 top-2 z-20 border-0 bg-amber-500 text-white"
              data-testid={`badge-gluten-free-${item.id}`}
            >
              Gluten-free
            </Badge>
          )}

          {imageLocationLabel && (
            <div
              className="absolute bottom-2 left-2 z-20 rounded-full bg-background/85 px-2 py-0.5 text-[9px] font-medium text-foreground shadow-sm backdrop-blur sm:text-[10px]"
              data-testid={`badge-distance-${item.id}`}
            >
              {imageLocationLabel}
            </div>
          )}

          {isEbtEligible && (
            <Badge
              className="absolute bottom-2 right-2 z-20 gap-1 border-0 bg-emerald-600 text-white"
              data-testid={`badge-ebt-${item.id}`}
            >
              <Landmark className="h-2.5 w-2.5" />
              EBT
            </Badge>
          )}
        </div>

        <div className="relative flex min-w-0 flex-1 flex-col space-y-1.5 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.24),transparent_38%),linear-gradient(180deg,rgba(255,255,255,0.06),rgba(0,0,0,0))] p-2.5 sm:p-3">
          {isSoldOut && (
            <div
              className="absolute right-3 top-3 z-20 rotate-3 rounded-md bg-[#df6f3f] px-3 py-1.5 text-[14px] font-semibold uppercase tracking-wide text-white shadow"
              data-testid={`badge-soldout-${item.id}`}
            >
              Sold Out
            </div>
          )}

          <div className="min-w-0">
            <h3
              className="line-clamp-2 pr-1 text-[15px] font-semibold leading-tight tracking-tight sm:text-base"
              data-testid={`text-name-${item.id}`}
            >
              {displayItemName}
            </h3>
            {item.seller?.id ? (
              <Link
                href={`/u/${item.seller.id}?back=${encodeURIComponent(location + (typeof window !== "undefined" ? window.location.search : ""))}`}
                className="relative z-20 mt-0.5 inline-flex w-fit max-w-full items-center text-[11px] font-medium text-muted-foreground hover:text-foreground sm:text-xs"
                onClick={stopCardNavigation}
                data-testid={`link-seller-name-${item.id}`}
              >
                <span className="truncate">{sellerName}</span>
              </Link>
            ) : (
              <span className="mt-0.5 block truncate text-[11px] font-medium text-muted-foreground sm:text-xs">
                {sellerName}
              </span>
            )}
            {Boolean((item.seller as any)?.isFoundingMaker) && (
              <span className="mt-1 block">
                <FoundingMakerBadge size="sm" />
              </span>
            )}
          </div>

          <HeatBadge level={(item as any).heatLevel} />

          <div className="flex flex-wrap items-center gap-1.5">
            {soldByWeight && (
              <span className="text-[10px] font-medium text-muted-foreground sm:text-xs">
                from
              </span>
            )}
            {isRequestableStatus(item.status) && price <= 0 ? (
              // An on_request item is a declaration of what a maker makes, not
              // a priced listing. "$0.00" reads as broken or free.
              <span
                className="text-sm font-semibold text-muted-foreground"
                data-testid={`text-price-${item.id}`}
              >
                Ask for price
              </span>
            ) : (
              <span className="text-lg font-bold text-primary sm:text-xl" data-testid={`text-price-${item.id}`}>
                ${price.toFixed(2)}
              </span>
            )}
            {soldByWeight ? (
              weightRangeLabel && (
                <span className="text-[10px] text-muted-foreground sm:text-xs" data-testid={`text-weight-range-${item.id}`}>
                  {(sizeTier ? `${sizeTier} · ` : "") + weightRangeLabel}
                </span>
              )
            ) : (
              (item as any).unit && (
                <span className="text-[10px] text-muted-foreground sm:text-xs">
                  {(item as any).unit}
                </span>
              )
            )}

            {groceryAvg && (
              <span className="text-[10px] text-muted-foreground line-through sm:text-xs">
                Avg ${groceryAvg.toFixed(2)}
              </span>
            )}

            <span
              className={`text-[10px] sm:text-[11px] ${!isSoldOut ? "text-muted-foreground" : "text-destructive"}`}
              data-testid={`text-stock-${item.id}`}
            >
              {stockLabel}
            </span>

            {postedLabel && (
              <span className="text-[10px] sm:text-[11px] text-muted-foreground">
                {postedLabel}
              </span>
            )}

            {saveCount != null && saveCount > 0 && (
              <span className="text-[10px] sm:text-[11px] text-muted-foreground">
                {saveCount} saved
              </span>
            )}

            {sellerReviewsHref && (
              sellerReviewCount > 0 ? (
                <Link
                  href={sellerReviewsHref}
                  className="relative z-20 inline-flex items-center gap-0.5 rounded-full border border-amber-200/70 bg-amber-50 px-1.5 py-0 text-[10px] font-medium text-amber-700 hover:bg-amber-100"
                  onClick={stopCardNavigation}
                  data-testid={`seller-rating-${item.id}`}
                >
                  <Star className="h-2.5 w-2.5 fill-amber-500 text-amber-500" />
                  {sellerAverageRating.toFixed(1)}
                  <span className="text-muted-foreground">·{sellerReviewCount}</span>
                </Link>
              ) : (
                <Link
                  href={sellerReviewsHref}
                  className="relative z-20 inline-flex items-center rounded-full border border-primary/25 bg-primary/10 px-1.5 py-0 text-[9px] font-semibold uppercase tracking-wide text-primary"
                  onClick={stopCardNavigation}
                  data-testid={`seller-rating-new-${item.id}`}
                >
                  New
                </Link>
              )
            )}

            {savings && savings > 0 && (
              <Badge
                variant="outline"
                className="border-chart-2/30 bg-chart-2/10 px-1.5 py-0 text-[9px] text-chart-2 sm:text-[10px]"
              >
                Save ${savings.toFixed(2)}
              </Badge>
            )}
          </div>

          {supportLine && (
            <p
              className="line-clamp-1 text-[10px] leading-4 text-muted-foreground sm:text-[11px]"
              data-testid={`text-short-description-${item.id}`}
            >
              {supportLine}
            </p>
          )}

          <div className="mt-auto space-y-1.5 pt-0">
              {primaryOption && (
                <div
                  className="flex items-start gap-1.5 rounded-2xl border border-card-border/70 bg-background/55 px-2 py-1 sm:px-2.5"
                  data-testid={`fulfillment-option-${item.id}-${primaryOption.id}`}
                >
                  {primaryOption.fulfillmentMethod === "local_delivery" ? (
                    <Truck className="mt-0.5 h-3 w-3 flex-shrink-0 text-muted-foreground" />
                  ) : (
                    <MapPin className="mt-0.5 h-3 w-3 flex-shrink-0 text-muted-foreground" />
                  )}

                  <div className="min-w-0 leading-tight">
                    <div className="text-[10px] font-medium text-foreground sm:text-[11px]">
                      {primaryOption.buyerLabel}
                    </div>
                    {isMadeToOrder ? (
                      <div className="text-[9px] text-muted-foreground sm:text-[10px]">
                        {mtoLeadTimeDays
                          ? `After it's made · ~${mtoLeadTimeDays} days`
                          : "After it's made"}
                      </div>
                    ) : (
                      primaryOption.dayLabel && (
                        <div className="text-[9px] text-muted-foreground sm:text-[10px]">
                          {primaryOption.dayLabel}
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}

              {fulfillmentOptions.length > 1 && (
                <div className="pl-1 text-[9px] text-muted-foreground sm:text-[10px]">
                  +{fulfillmentOptions.length - 1} more
                </div>
              )}

            <div className="flex items-center justify-end gap-1.5">
              {onToggleSave && (
                <button
                  type="button"
                  className="relative z-30 flex h-8 w-8 items-center justify-center rounded-full border border-border bg-background/90 transition-transform active:scale-90"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setHeartPop(true);
                    setTimeout(() => setHeartPop(false), 300);
                    onToggleSave(item.id);
                  }}
                  aria-label={isSaved ? "Unsave" : "Save"}
                >
                  <Heart
                    className={[
                      "h-4 w-4 transition-transform duration-200",
                      isSaved ? "fill-red-500 text-red-500" : "text-muted-foreground",
                      heartPop ? "scale-125" : "scale-100",
                    ].join(" ")}
                  />
                </button>
              )}

              {(item.seller as any)?.isDemoSeller ? (
                <span className="relative z-30 inline-flex h-8 items-center rounded-full bg-muted px-3 text-[11px] font-medium text-muted-foreground">
                  Sample
                </span>
              ) : soldByWeight ? (
                <Button
                  size="sm"
                  className="relative z-30 h-8 rounded-full px-3.5 text-[12px]"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    if (isSoldOut) {
                      goToProductDetail();
                      return;
                    }
                    setSizePickerOpen(true);
                  }}
                  data-testid={`button-choose-size-${item.id}`}
                >
                  {isSoldOut ? "Sold out" : "Choose size"}
                </Button>
              ) : isMadeToOrder ? (
                <Button
                  size="sm"
                  variant="outline"
                  className="relative z-30 h-8 rounded-full border-primary/50 bg-primary/10 px-3 text-[11px] font-semibold text-primary hover:bg-primary/20"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    goToProductDetail();
                  }}
                  data-testid={`button-customize-${item.id}`}
                >
                  Customize
                </Button>
              ) : (item as any).growthState === "growing" ||
                Number((item as any).isMeatShare ?? 0) === 1 ? (
                <Button
                  size="sm"
                  variant="outline"
                  className="relative z-30 h-8 rounded-full border-emerald-500/50 bg-emerald-50 px-3 text-[11px] font-semibold text-emerald-800 hover:bg-emerald-100"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    goToProductDetail();
                  }}
                  data-testid={`button-reserve-${item.id}`}
                >
                  {Number((item as any).isMeatShare ?? 0) === 1 ? (
                    "Reserve share"
                  ) : (
                    <>
                      <Sprout className="mr-1 h-3 w-3" />
                      Reserve
                    </>
                  )}
                </Button>
              ) : isRequestableStatus(item.status) ? (
                <Button
                  size="sm"
                  variant="outline"
                  className="relative z-30 h-8 rounded-full border-brand-secondary/40 bg-brand-secondary/10 px-3 text-[11px] font-semibold text-brand-secondary"
                  onClick={async (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    try {
                      const res = await fetch(`/api/items/${item.id}/notify-me`, {
                        method: "POST",
                        credentials: "include",
                      });
                      if (res.ok) setNotifyOpen(true);
                    } catch {
                      /* swallow — user can retry */
                    }
                  }}
                  data-testid={`button-notify-${item.id}`}
                >
                  {/* Same signup either way. The wording differs because the
                      seller's next move differs: pending-payments needs Stripe
                      finished, on-request/archived needs them to make it. */}
                  {item.status === "published_pending_payments" ? "Notify me" : "Request this"}
                </Button>
              ) : (
                <Button
                  size="sm"
                  className="relative z-30 h-8 rounded-full px-3.5 text-[12px]"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    handleAddToCart();
                  }}
                  disabled={item.qtyAvailable <= 0 || atStockCap}
                  data-mission-target="add-to-cart"
                  data-testid={`button-add-cart-${item.id}`}
                >
                  {showCheck ? <Check className="h-4 w-4" /> : "Add"}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
      {soldByWeight && (
        <div onClick={stopCardNavigation}>
          <WeightSizePicker
            open={sizePickerOpen}
            onOpenChange={setSizePickerOpen}
            itemId={item.id}
            tierLabel={sizeTier}
            itemName={displayItemName}
            onSelected={(unit) => {
              onAddToCart(item.id, 1, undefined, unit);
              setShowCheck(true);
              setTimeout(() => setShowCheck(false), 500);
            }}
          />
        </div>
      )}
      <AlertDialog open={notifyOpen} onOpenChange={setNotifyOpen}>
        <AlertDialogContent onClick={(e) => e.stopPropagation()} className="max-w-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>You're on the list 🌱</AlertDialogTitle>
            <AlertDialogDescription>
              We let {sellerName} know you want {displayItemName}. They're finishing their
              payment setup, and the moment they can accept orders we'll notify you so you can
              grab it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={(e) => e.stopPropagation()}>OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );

}
